import * as React from 'react';
import { 
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalContent,
  CloseButton,
  DeviceFrame,
  ModeSelector,
  CourseGrid,
  CourseCard,
  PosterImage,
  CoverImage,
  CourseInfo,
  CourseTitle,
  CourseDescription,
  PreviewToggle,
  InfoText
} from './MobilePreview.style';
import { MobilePreviewProps, MobilePreviewCourse } from './MobilePreview.type';
import { useMobilePreview } from './MobilePreview.use-case';

export const MobilePreviewComponent: React.FC<MobilePreviewProps> = (props) => {
  const _useMobilePreview = useMobilePreview(props);

  React.useEffect(() => {
    if (!_useMobilePreview.shouldShowPreview) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && _useMobilePreview.isVisible) {
        _useMobilePreview.togglePreview();
      }
    };

    if (_useMobilePreview.isVisible) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [_useMobilePreview.shouldShowPreview, _useMobilePreview.isVisible, _useMobilePreview.togglePreview]);

  if (!_useMobilePreview.shouldShowPreview) {
    return null;
  }

  const renderCourseCard = (course: MobilePreviewCourse, index: number) => (
    <CourseCard key={course.id} isFirst={index === 0}>
      {(_useMobilePreview.mode === 'poster' || _useMobilePreview.mode === 'both') && course.poster && (
        <PosterImage
          src={course.poster.uri}
          alt={course.title}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/images/image-default-courses.png';
          }}
        />
      )}
      
      {_useMobilePreview.mode === 'cover' && course.cover && (
        <CoverImage
          src={course.cover.uri}
          alt={course.title}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/images/image-default-courses.png';
          }}
        />
      )}

      {_useMobilePreview.mode === 'both' && course.cover && (
        <CoverImage
          src={course.cover.uri}
          alt={`${course.title} - Cover`}
          loading="lazy"
          style={{ marginTop: course.poster ? '4px' : '0' }}
          onError={(e) => {
            e.currentTarget.src = '/images/image-default-courses.png';
          }}
        />
      )}

      <CourseInfo>
        <CourseTitle>{course.title}</CourseTitle>
        <CourseDescription>{course.description}</CourseDescription>
      </CourseInfo>
    </CourseCard>
  );

  const handlePreviewClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    _useMobilePreview.togglePreview();
  };

  return (
    <>
      <PreviewToggle 
        onClick={handlePreviewClick}
      >
        <i className="fal fa-mobile-alt"></i>
        Ver Preview Mobile
      </PreviewToggle>

      <ModalOverlay 
        isOpen={_useMobilePreview.isVisible} 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            e.stopPropagation();
            _useMobilePreview.togglePreview();
          }
        }}
      >
        <ModalContainer>
          <ModalHeader>
            <h2>📱 {_useMobilePreview.modeTitle}</h2>
            <CloseButton 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                _useMobilePreview.togglePreview();
              }}
            >
              <i className="fal fa-times"></i>
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <ModeSelector>
              <button 
                type="button"
                className={_useMobilePreview.mode === 'poster' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  _useMobilePreview.changeMode('poster');
                }}
              >
                🖼️ Poster
              </button>
              <button 
                type="button"
                className={_useMobilePreview.mode === 'cover' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  _useMobilePreview.changeMode('cover');
                }}
              >
                🎬 Cover
              </button>
              <button 
                type="button"
                className={_useMobilePreview.mode === 'both' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  _useMobilePreview.changeMode('both');
                }}
              >
                🎯 Ambos
              </button>
            </ModeSelector>

            <DeviceFrame>
              <CourseGrid mode={_useMobilePreview.mode}>
                {_useMobilePreview.sampleCourses.map(renderCourseCard)}
              </CourseGrid>
            </DeviceFrame>

            <InfoText>
              {_useMobilePreview.mode === 'poster' && '📐 Formato Poster (2:3) - Ideal para listas de cursos em grade'}
              {_useMobilePreview.mode === 'cover' && '📐 Formato Cover (16:9) - Ideal para banners e destaques horizontais'}
              {_useMobilePreview.mode === 'both' && '📐 Visualização completa - Como ficará no app com ambos os formatos'}
            </InfoText>
          </ModalContent>
        </ModalContainer>
      </ModalOverlay>
    </>
  );
};
